import { useTranslation } from '../i18n';
import { COLORS } from '../lib/theme';
import { useTheme } from '../lib/themeMode';
import { Icon } from "./Icon";

/**
 * The light/dark switch. State lives in the shared theme store (lib/themeMode),
 * so every toggle on the page — and every link that carries the theme to the
 * web shop — follows a flip from any one of them.
 */
export function ThemeToggle({ collapsed }: { collapsed?: boolean }) {
  const { t } = useTranslation('nav');
  const { isDark, toggleTheme } = useTheme();

  const label = isDark ? t('theme.switchToLight') : t('theme.switchToDark');

  if (collapsed) {
    return (
      <button
        onClick={toggleTheme}
        aria-label={label}
        title={label}
        style={{
          width: 40,
          height: 40,
          borderRadius: 8,
          background: "transparent",
          color: COLORS.onSurface,
          border: "none",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon name={isDark ? "sun" : "moon"} size={18} />
      </button>
    );
  }

  return (
    <button
      onClick={toggleTheme}
      aria-label={label}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        height: 40,
        padding: "0 12px",
        borderRadius: 6,
        background: "transparent",
        border: "none",
        color: COLORS.onSurface,
        fontSize: 14,
        fontWeight: 500,
        width: "100%",
        textAlign: "left",
      }}
    >
      <Icon name={isDark ? "sun" : "moon"} size={18} />
      <span style={{ flex: 1 }}>{isDark ? t('theme.light') : t('theme.dark')}</span>
      <span
        aria-hidden
        style={{
          width: 52,
          height: 32,
          boxSizing: "border-box",
          borderRadius: 999,
          border: isDark ? "2px solid transparent" : `2px solid ${COLORS.outline}`,
          background: isDark ? COLORS.primary : COLORS.surfaceContainerHighest,
          position: "relative",
          flexShrink: 0,
          display: "inline-flex",
          transition: "background 160ms ease, border-color 160ms ease",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: isDark ? 22 : 2,
            width: 24,
            height: 24,
            borderRadius: 999,
            background: isDark ? COLORS.primaryFixed : COLORS.outline,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: isDark ? COLORS.onPrimaryFixed : COLORS.surfaceContainerHighest,
            transition: "left 160ms ease, background 160ms ease, color 160ms ease",
          }}
        >
          {isDark ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4.5 4.5L11.5 11.5M11.5 4.5L4.5 11.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
            </svg>
          )}
        </span>
      </span>
    </button>
  );
}
